import React, { useState, useEffect } from 'react';
import VintageHeader from './VintageHeader';
import VintageFooter from './VintageFooter';
import LoadingSpinner from '../ui/LoadingSpinner';
import { useDataService } from '../../context/DataContext';
import useAdmin from '../../hooks/useAdmin';

/**
 * VINTAGE LAYOUT COMPONENT - FIXED HEADER INTEGRATION
 * 
 * Purpose: Consistent vintage aesthetic with proper admin integration
 * - Fixed header overlap issues
 * - Proper admin button placement
 * - Maintains typewriter theme throughout
 */

const VintageLayout = ({ children }) => {
  const [profileData, setProfileData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  
  const dataService = useDataService();
  const { isAdmin } = useAdmin();

  useEffect(() => {
    const loadLayoutData = async () => {
      try {
        setLoading(true);
        const [profile, categoriesData] = await Promise.all([
          dataService.getProfile(),
          dataService.getCategories()
        ]);
        
        setProfileData(profile);
        setCategories(categoriesData);
      } catch (err) {
        setError(err.message);
        console.error('Error loading layout data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadLayoutData();
  }, [dataService]);

  const handleAdminClick = () => {
    setShowAdminPanel(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-vintage-cream flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <p className="typewriter text-vintage-faded mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-vintage-cream flex items-center justify-center">
        <div className="text-center academic-paper">
          <h2 className="vintage-heading-2 text-2xl text-vintage-ink mb-4">
            Unable to load the website
          </h2>
          <p className="vintage-body text-vintage-faded mb-6">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="vintage-btn"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-vintage-cream">
      <VintageHeader 
        profileData={profileData} 
        categories={categories}
        onAdminClick={handleAdminClick}
      />
      <main>
        {/* Pass admin handler to children if needed */}
        {React.cloneElement(children, { onAdminClick: handleAdminClick })}
      </main>
      <VintageFooter profileData={profileData} />
    </div>
  );
};

export default VintageLayout;